<script setup>
// Construtor de e-mails (tela cheia): trilho à esquerda (blocos ou texto,
// marca, IA), preview renderizado no servidor no meio, bloco selecionado à
// direita. Autosave + undo/redo + envio de teste pela caixa de e-mail.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useEmailBuilder } from './useEmailBuilder';
import EmailPreview from './EmailPreview.vue';
import EmailBlocksPanel from './EmailBlocksPanel.vue';
import EmailBlockInspector from './EmailBlockInspector.vue';
import EmailBrandPanel from './EmailBrandPanel.vue';
import EmailAiPanel from './EmailAiPanel.vue';
import PersonalEditor from './PersonalEditor.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const onError = error => {
  useAlert(error?.response?.data?.error || t('EMAIL_TEMPLATES.SAVE_ERROR'));
};
const b = useEmailBuilder({ templateId: route.params.templateId, onError });
const {
  template,
  catalog,
  loading,
  selectedId,
  selectedBlock,
  blocks,
  isPersonal,
  device,
  dark,
  saveState,
  preview,
  canUndo,
  canRedo,
} = b;

const rail = ref('blocks'); // blocks | brand | ai
const railTabs = computed(() => ['blocks', 'brand', 'ai']);
const hasContent = computed(() =>
  isPersonal.value
    ? !!template.value?.personal_text?.trim()
    : blocks.value.some(
        x => !['header', 'signature', 'footer'].includes(x.kind)
      )
);

const goBack = async () => {
  await b.flush();
  router.push({
    name: 'email_templates_index',
    params: { accountId: route.params.accountId },
  });
};

// Trocar de modo: o bloco selecionado pode nem existir no outro modo.
const changeMode = mode => {
  selectedId.value = null;
  rail.value = 'blocks';
  b.updateTemplate({ mode });
};

const saveNow = async () => {
  if (await b.save()) useAlert(t('EMAIL_TEMPLATES.SAVED'));
};

// ---- teste ----
const testDialog = ref(null);
const testInboxId = ref(null);
const sendingTest = ref(false);
const emailInboxes = computed(
  () => store.getters['inboxes/getEmailInboxes'] || []
);
const openTest = async () => {
  await b.flush();
  testInboxId.value = testInboxId.value || emailInboxes.value[0]?.id || null;
  testDialog.value?.open();
};
const sendTest = async () => {
  if (!testInboxId.value) return;
  sendingTest.value = true;
  try {
    const { data } = await API.sendTest(template.value.id, testInboxId.value);
    useAlert(t('EMAIL_TEMPLATES.BUILDER.TEST.SENT', { email: data.sent_to }));
    testDialog.value?.close();
  } catch (error) {
    onError(error);
  } finally {
    sendingTest.value = false;
  }
};

const applyKit = async () => {
  try {
    const { data } = await API.applyBrandKit(template.value.id);
    template.value.settings = data.settings;
    template.value.theme = data.theme;
    b.refreshPreview();
    useAlert(t('EMAIL_TEMPLATES.BUILDER.BRAND.APPLIED'));
  } catch (error) {
    onError(error);
  }
};

const onAiApply = result => {
  b.applyAiResult(result);
};

const onKey = event => {
  const mod = event.ctrlKey || event.metaKey;
  if (!mod) return;
  const tag = event.target?.tagName;
  const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag);
  if (event.key.toLowerCase() === 's') {
    event.preventDefault();
    saveNow();
  } else if (event.key.toLowerCase() === 'z' && !typing) {
    event.preventDefault();
    if (event.shiftKey) b.redo();
    else b.undo();
  }
};

onMounted(() => {
  b.load();
  store.dispatch('inboxes/get');
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  b.flush();
});

const stateLabel = computed(() =>
  t(`EMAIL_TEMPLATES.BUILDER.SAVE_STATE.${saveState.value}`)
);
const inputClass =
  'rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand';
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <!-- Topo: linha 1 = identidade do e-mail; linha 2 = preview e ações -->
    <header
      class="flex flex-col gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2"
    >
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex shrink-0 items-center gap-1 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left" /> {{ t('EMAIL_TEMPLATES.BACK') }}
        </button>
        <template v-if="template">
          <input
            :value="template.name"
            :placeholder="t('EMAIL_TEMPLATES.NAME')"
            :class="inputClass"
            class="w-56 shrink-0"
            @input="b.updateTemplate({ name: $event.target.value })"
          />
          <input
            :value="template.subject"
            :placeholder="t('EMAIL_TEMPLATES.SUBJECT')"
            :class="inputClass"
            class="min-w-0 flex-1"
            @input="b.updateTemplate({ subject: $event.target.value })"
          />
          <select
            :value="template.mode"
            :class="inputClass"
            class="!w-40 shrink-0"
            @change="changeMode($event.target.value)"
          >
            <option value="newsletter">
              {{ t('EMAIL_TEMPLATES.BUILDER.MODE.newsletter') }}
            </option>
            <option value="personal">
              {{ t('EMAIL_TEMPLATES.BUILDER.MODE.personal') }}
            </option>
          </select>
        </template>
      </div>
      <div v-if="template" class="flex items-center gap-2">
        <div
          class="flex items-center gap-0.5 rounded-lg border border-n-weak p-0.5"
        >
          <button
            type="button"
            class="rounded-md px-2 py-1 text-xs"
            :class="
              device === 'desktop'
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11'
            "
            @click="device = 'desktop'"
          >
            {{ t('EMAIL_TEMPLATES.BUILDER.PREVIEW.DESKTOP') }}
          </button>
          <button
            type="button"
            class="rounded-md px-2 py-1 text-xs"
            :class="
              device === 'mobile'
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11'
            "
            @click="device = 'mobile'"
          >
            {{ t('EMAIL_TEMPLATES.BUILDER.PREVIEW.MOBILE') }}
          </button>
          <button
            type="button"
            class="rounded-md px-2 py-1 text-xs"
            :class="dark ? 'bg-n-brand/10 text-n-brand' : 'text-n-slate-11'"
            @click="dark = !dark"
          >
            {{ t('EMAIL_TEMPLATES.BUILDER.PREVIEW.DARK') }}
          </button>
        </div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="i-lucide-undo-2 text-n-slate-11 disabled:opacity-30"
            :disabled="!canUndo"
            :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.UNDO')"
            @click="b.undo()"
          />
          <button
            type="button"
            class="i-lucide-redo-2 text-n-slate-11 disabled:opacity-30"
            :disabled="!canRedo"
            :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REDO')"
            @click="b.redo()"
          />
        </div>
        <span
          class="text-[11px]"
          :class="saveState === 'error' ? 'text-n-ruby-9' : 'text-n-slate-10'"
          >{{ stateLabel }}</span
        >
        <span class="flex-1" />
        <button
          type="button"
          class="rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-11 hover:bg-n-solid-2"
          @click="openTest"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.TEST') }}
        </button>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          :disabled="saveState === 'saving'"
          @click="saveNow"
        >
          {{ t('EMAIL_TEMPLATES.SAVE') }}
        </button>
      </div>
    </header>

    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('EMAIL_TEMPLATES.LOADING') }}
    </div>

    <div v-else-if="template" class="flex min-h-0 flex-1">
      <!-- Trilho esquerdo -->
      <aside
        class="flex shrink-0 flex-col border-r border-n-weak bg-n-solid-1"
        :class="isPersonal && rail === 'blocks' ? 'w-[420px]' : 'w-72'"
      >
        <div class="flex border-b border-n-weak">
          <button
            v-for="tab in railTabs"
            :key="tab"
            type="button"
            class="flex-1 border-b-2 py-2 text-xs font-semibold"
            :class="
              rail === tab
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-10'
            "
            @click="rail = tab"
          >
            {{
              tab === 'blocks' && isPersonal
                ? t('EMAIL_TEMPLATES.BUILDER.RAIL.TEXT')
                : t(`EMAIL_TEMPLATES.BUILDER.RAIL.${tab.toUpperCase()}`)
            }}
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto">
          <template v-if="rail === 'blocks'">
            <PersonalEditor
              v-if="isPersonal"
              :text="template.personal_text"
              @update="v => b.updateTemplate({ personal_text: v })"
            />
            <EmailBlocksPanel
              :catalog="catalog"
              :blocks="blocks"
              :selected-id="selectedId"
              :palette="!isPersonal"
              @add="kind => b.addBlock(kind)"
              @select="id => (selectedId = id)"
              @move="(id, dir) => b.moveBlock(id, dir)"
              @duplicate="id => b.duplicateBlock(id)"
              @remove="id => b.removeBlock(id)"
            />
          </template>
          <EmailBrandPanel
            v-else-if="rail === 'brand'"
            :template="template"
            @theme="p => b.updateTheme(p)"
            @settings="p => b.updateSettings(p)"
            @apply-kit="applyKit"
          />
          <EmailAiPanel
            v-else
            :template="template"
            :source="b.source"
            :has-content="hasContent"
            @apply="onAiApply"
            @subject="s => b.updateTemplate({ subject: s })"
          />
        </div>
      </aside>

      <!-- Preview -->
      <EmailPreview
        :html="preview.html"
        :device="device"
        :dark="dark"
        :loading="preview.loading"
        :size-kb="preview.sizeKb"
      />

      <!-- Inspector -->
      <aside
        v-if="selectedBlock"
        class="w-80 shrink-0 overflow-y-auto border-l border-n-weak bg-n-solid-1"
      >
        <EmailBlockInspector
          :key="selectedBlock.id"
          :block="selectedBlock"
          :catalog="catalog"
          :theme="template.theme || {}"
          @update="p => b.updateSelected(p)"
          @remove="b.removeBlock(selectedBlock.id)"
          @duplicate="b.duplicateBlock(selectedBlock.id)"
        />
      </aside>
    </div>

    <!-- Envio de teste -->
    <Dialog
      ref="testDialog"
      type="edit"
      :title="t('EMAIL_TEMPLATES.BUILDER.TEST.TITLE')"
      :description="t('EMAIL_TEMPLATES.BUILDER.TEST.DESCRIPTION')"
      :confirm-button-label="t('EMAIL_TEMPLATES.BUILDER.TEST.SEND')"
      :is-loading="sendingTest"
      :disable-confirm-button="!testInboxId"
      @confirm="sendTest"
    >
      <p v-if="!emailInboxes.length" class="text-sm text-n-amber-11">
        {{ t('EMAIL_TEMPLATES.BUILDER.TEST.NO_INBOX') }}
      </p>
      <select v-else v-model="testInboxId" :class="inputClass" class="w-full">
        <option v-for="ib in emailInboxes" :key="ib.id" :value="ib.id">
          {{ ib.name }}
        </option>
      </select>
    </Dialog>
  </div>
</template>
